import FilterListIcon from "@mui/icons-material/FilterList";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import RefreshIcon from "@mui/icons-material/Refresh";
import SensorsIcon from "@mui/icons-material/Sensors";
import { Autocomplete, IconButton, Popover, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import { FilterOption } from "@/components/screens/ServiceDashboard/components/DashboardHeader";
import { ServiceDashboardMessages } from "@/messages/serviceDashboard/service-dashboard.messages";

/** Calendar days (YYYY-MM-DD), both inclusive. */
export type DateRangeValue = { startDate: string; endDate: string };

type FilterField = {
  label: string;
  options: FilterOption[];
  value: FilterOption[];
  onChange: (value: FilterOption[]) => void;
};

type Props = {
  messages: ServiceDashboardMessages;
  selectedRange: DateRangeValue;
  onChangeRange: (value: DateRangeValue) => void;
  filters: FilterField[];
  onApplyFilters: () => void;
  onClearFilters: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  autoRefresh: boolean;
  onToggleAutoRefresh: (value: boolean) => void;
  lastUpdatedAt: Date | null;
  isFullscreen: boolean;
  onFullscreen: () => void;
};

const autocompleteSx = { "& .MuiOutlinedInput-root": { paddingY: "0.15rem" } };

const dateSx = {
  width: "9.5rem",
  "& .MuiInputBase-root": { height: "2.25rem", borderRadius: "0.5rem" },
  "& .MuiInputBase-input": {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: (theme: { palette: { dark: { main: string } } }) => theme.palette.dark.main,
  },
  "& .MuiInputAdornment-root .MuiSvgIcon-root": {
    color: (theme: { palette: { dark: { main: string } } }) => theme.palette.dark.main,
  },
  "& .MuiOutlinedInput-notchedOutline, & .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline":
    {
      borderWidth: "1px !important",
      borderColor: (theme: { palette: { dark: { main: string } } }) =>
        `${theme.palette.dark.main} !important`,
    },
};

const ManagerialHeader = ({
  messages,
  selectedRange,
  onChangeRange,
  filters,
  onApplyFilters,
  onClearFilters,
  onRefresh,
  isRefreshing,
  lastUpdatedAt,
  autoRefresh,
  onToggleAutoRefresh,
  isFullscreen,
  onFullscreen,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const activeFilters = filters.reduce((sum, filter) => sum + filter.value.length, 0) || undefined;
  const formattedUpdate = lastUpdatedAt
    ? new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(lastUpdatedAt)
    : "--:--";

  const buttonSx = {
    textTransform: "none",
    height: "2.25rem",
    borderRadius: "0.5rem",
    fontWeight: 600,
  };

  return (
    <MDBox
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      flexWrap="wrap"
      gap={1}
      mb={1.5}
    >
      <MDBox>
        <MDTypography variant="h4" fontWeight="bold" lineHeight={1.2}>
          {messages.managerialPageTitle}
        </MDTypography>
        <MDTypography variant="button" color="text" fontWeight="regular">
          {messages.managerialSubtitle}
        </MDTypography>
      </MDBox>

      <MDBox display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
        <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <DatePicker
              value={dayjs(selectedRange.startDate)}
              onChange={(value) => {
                if (!value || !value.isValid()) return;
                const startDate = value.format("YYYY-MM-DD");
                const endDate =
                  startDate > selectedRange.endDate ? startDate : selectedRange.endDate;
                onChangeRange({ startDate, endDate });
              }}
              format="DD/MM/YYYY"
              maxDate={dayjs(selectedRange.endDate)}
              slotProps={{ textField: { size: "small", sx: dateSx } }}
            />
            <MDTypography variant="button" color="text">
              -
            </MDTypography>
            <DatePicker
              value={dayjs(selectedRange.endDate)}
              onChange={(value) => {
                if (!value || !value.isValid()) return;
                const endDate = value.format("YYYY-MM-DD");
                const startDate =
                  endDate < selectedRange.startDate ? endDate : selectedRange.startDate;
                onChangeRange({ startDate, endDate });
              }}
              format="DD/MM/YYYY"
              minDate={dayjs(selectedRange.startDate)}
              disableFuture
              slotProps={{ textField: { size: "small", sx: dateSx } }}
            />
          </MDBox>

          <MDButton
            variant={activeFilters ? "gradient" : "outlined"}
            color={activeFilters ? "primary" : "dark"}
            size="small"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={buttonSx}
          >
            <FilterListIcon fontSize="small" />
            &nbsp;{messages.filtersButton}
            {activeFilters ? ` (${activeFilters})` : ""}
          </MDButton>

          <MDButton variant="outlined" color="dark" size="small" onClick={onRefresh} sx={buttonSx}>
            <RefreshIcon fontSize="small" />
            &nbsp;{messages.refreshButton}
          </MDButton>

          <MDButton
            variant={autoRefresh ? "gradient" : "outlined"}
            color={autoRefresh ? "success" : "dark"}
            size="small"
            onClick={() => onToggleAutoRefresh(!autoRefresh)}
            sx={buttonSx}
          >
            <SensorsIcon fontSize="small" />
            &nbsp;{messages.realtimeLabel}
          </MDButton>

          {!isFullscreen && (
            <MDButton variant="outlined" color="dark" size="small" onClick={onFullscreen} sx={buttonSx}>
              <FullscreenIcon fontSize="small" />
            </MDButton>
          )}
        </MDBox>
        <MDBox display="flex" alignItems="center" gap={0.5}>
          <MDTypography variant="caption" color="text">
            {`${messages.lastUpdatedLabel} ${formattedUpdate}`}
          </MDTypography>
          <IconButton size="small" onClick={onRefresh} sx={{ p: 0.25 }}>
            <RefreshIcon
              sx={{
                fontSize: "0.9rem !important",
                animation: isRefreshing ? "dashSpin 1s linear infinite" : "none",
                "@keyframes dashSpin": { to: { transform: "rotate(360deg)" } },
              }}
            />
          </IconButton>
        </MDBox>
      </MDBox>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              borderRadius: "0.75rem",
              mt: 1,
              backgroundColor: theme.palette.background.paper,
              backgroundImage: "none",
              opacity: 1,
              boxShadow: theme.shadows[8],
            }),
          },
        }}
      >
        <MDBox p={2} width={{ xs: "18rem", sm: "22rem" }} display="grid" gap={1.5}>
          {filters.map((filter) => (
            <Autocomplete
              key={filter.label}
              multiple
              size="small"
              sx={autocompleteSx}
              options={filter.options}
              getOptionLabel={(option) => option.name}
              value={filter.value}
              onChange={(_, value) => filter.onChange(value as FilterOption[])}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label={filter.label} />}
            />
          ))}
          <MDBox display="flex" gap={1}>
            <MDButton
              variant="outlined"
              color="dark"
              size="small"
              fullWidth
              sx={{ textTransform: "none" }}
              onClick={onClearFilters}
            >
              {messages.clearFiltersButton}
            </MDButton>
            <MDButton
              variant="gradient"
              color="primary"
              size="small"
              fullWidth
              sx={{ textTransform: "none" }}
              onClick={() => {
                onApplyFilters();
                setAnchorEl(null);
              }}
            >
              {messages.applyFiltersButton}
            </MDButton>
          </MDBox>
        </MDBox>
      </Popover>
    </MDBox>
  );
};

export default ManagerialHeader;
